import { useState, useEffect, useMemo, useCallback, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, toast } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Sparkles, Loader2, TrendingUp, RefreshCw, Calendar
} from "lucide-react";
import { formatVolume, formatPrice } from "@/hooks/tickMerge";
import { isMarketOpen } from "@/utils/marketHours";
import useRealtimeData from "@/hooks/useRealtimeData";
import { useRestOnce } from "@/hooks/useRestOnce";
import { mergeTicksIntoList } from "@/hooks/tickMerge";
import StockLogo from "@/components/StockLogo";
import CommodityLogoShared from "@/components/CommodityLogo";
import InstrumentRow from "@/components/InstrumentRow";
import { RowHoverActions } from "@/components/RowHoverActions";
import SEOHead, { PageSEO } from "@/components/SEOHead";
import { useWatchlistBatch } from "@/hooks/useWatchlist";
import { ConnectionBanner } from "@/components/StaleIndicator";
import { LiveOverlayIndicator } from "@/components/LiveOverlayIndicator";
import { useCriticalLogos } from "@/hooks/useCriticalLogos";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatExpiryFromISO, sortByContracts, reRankByTicks } from "@/utils/futuresRanking";
// TradingTabs import kept for reference - Layout handles tab navigation

// Known symbols that TradingView supports reliably for Indian markets
// Index mapping for TradingView — use centralized mapping
import { openChartSafe as openChartGlobal } from '../utils/tradingViewMapping';

/**
 * Open chart in a new tab — TradingView for known symbols,
 * in-app StoxCap chart for unsupported symbols (small-caps, MCX, etc.).
 */
const openChartSafe = openChartGlobal;

// Helper function to get next monthly expiry (last Thursday of month)

const getNextMonthlyExpiry = () => {
  const now = new Date();
  let month = now.getMonth();
  let year = now.getFullYear();
  
  // Find last Thursday of current month
  const lastDay = new Date(year, month + 1, 0);
  let lastThursday = new Date(lastDay);
  while (lastThursday.getDay() !== 4) {
    lastThursday.setDate(lastThursday.getDate() - 1);
  }
  
  // If current date is past last Thursday, get next month's expiry
  if (now > lastThursday) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
    const nextLastDay = new Date(year, month + 1, 0);
    lastThursday = new Date(nextLastDay);
    while (lastThursday.getDay() !== 4) {
      lastThursday.setDate(lastThursday.getDate() - 1);
    }
  }
  
  // Format as "27 Mar" style
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${lastThursday.getDate()} ${months[lastThursday.getMonth()]}`;
};

// Index logo component - Professional logos like StoxCap
const IndexLogo = ({ symbol, size = "lg" }) => {
  const s = size === "lg" ? "w-12 h-12" : "w-10 h-10";
  const fs = size === "lg" ? "text-[10px]" : "text-[9px]";
  
  // NIFTY 50 - Blue NSE style
  if (symbol === 'NIFTY50' || symbol === 'NIFTY') {
    return (
      <div className={`${s} rounded-xl bg-[#004B8D] flex items-center justify-center shadow-sm overflow-hidden`}>
        <svg viewBox="0 0 48 48" className="w-full h-full">
          <rect width="48" height="48" fill="#004B8D"/>
          <path d="M8 32 L14 18 L20 26 L26 14 L32 22 L38 12" stroke="#FFD700" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <text x="24" y="42" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" fontFamily="Arial">NIFTY 50</text>
        </svg>
      </div>
    );
  }
  
  // BANK NIFTY - Green banking theme
  if (symbol === 'BANKNIFTY') {
    return (
      <div className={`${s} rounded-xl bg-[#1B5E20] flex items-center justify-center shadow-sm overflow-hidden`}>
        <svg viewBox="0 0 48 48" className="w-full h-full">
          <rect width="48" height="48" fill="#1B5E20"/>
          <path d="M24 8 L38 16 L38 18 L10 18 L10 16 Z" fill="#A5D6A7"/>
          <rect x="13" y="20" width="4" height="12" rx="1" fill="#A5D6A7"/>
          <rect x="22" y="20" width="4" height="12" rx="1" fill="#A5D6A7"/>
          <rect x="31" y="20" width="4" height="12" rx="1" fill="#A5D6A7"/>
          <rect x="10" y="33" width="28" height="3" rx="1" fill="#A5D6A7"/>
          <text x="24" y="44" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="Arial">BANK NIFTY</text>
        </svg>
      </div>
    );
  }
  
  // SENSEX - Red BSE theme
  if (symbol === 'SENSEX') {
    return (
      <div className={`${s} rounded-xl bg-[#B71C1C] flex items-center justify-center shadow-sm overflow-hidden`}>
        <svg viewBox="0 0 48 48" className="w-full h-full">
          <rect width="48" height="48" fill="#B71C1C"/>
          <path d="M10 32 L16 24 L22 28 L28 16 L34 20 L40 10" stroke="#FFCDD2" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="40" cy="10" r="2.5" fill="#FF5252"/>
          <text x="24" y="43" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" fontFamily="Arial">SENSEX</text>
        </svg>
      </div>
    );
  }
  
  // MIDCAP NIFTY - Purple theme
  if (symbol === 'MIDCPNIFTY') {
    return (
      <div className={`${s} rounded-xl bg-[#4A148C] flex items-center justify-center shadow-sm overflow-hidden`}>
        <svg viewBox="0 0 48 48" className="w-full h-full">
          <rect width="48" height="48" fill="#4A148C"/>
          <rect x="10" y="26" width="5" height="10" rx="1" fill="#CE93D8"/>
          <rect x="17" y="20" width="5" height="16" rx="1" fill="#BA68C8"/>
          <rect x="24" y="22" width="5" height="14" rx="1" fill="#AB47BC"/>
          <rect x="31" y="14" width="5" height="22" rx="1" fill="#9C27B0"/>
          <text x="24" y="44" textAnchor="middle" fill="white" fontSize="6.5" fontWeight="bold" fontFamily="Arial">MIDCAP</text>
        </svg>
      </div>
    );
  }
  
  // FINNIFTY - Amber financial theme
  if (symbol === 'FINNIFTY') {
    return (
      <div className={`${s} rounded-xl bg-[#E65100] flex items-center justify-center shadow-sm overflow-hidden`}>
        <svg viewBox="0 0 48 48" className="w-full h-full">
          <rect width="48" height="48" fill="#E65100"/>
          <text x="24" y="16" textAnchor="middle" fill="#FFE0B2" fontSize="14" fontWeight="bold" fontFamily="Arial">&#8377;</text>
          <path d="M12 30 L18 22 L24 26 L30 18 L36 14" stroke="#FFE0B2" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <text x="24" y="44" textAnchor="middle" fill="white" fontSize="6.5" fontWeight="bold" fontFamily="Arial">FIN NIFTY</text>
        </svg>
      </div>
    );
  }
  
  // BANKEX - BSE Bank Index (teal banking theme)
  if (symbol === 'BANKEX') {
    return (
      <div className={`${s} rounded-xl bg-[#00695C] flex items-center justify-center shadow-sm overflow-hidden`}>
        <svg viewBox="0 0 48 48" className="w-full h-full">
          <rect width="48" height="48" fill="#00695C"/>
          <path d="M24 8 L36 15 L36 17 L12 17 L12 15 Z" fill="#80CBC4"/>
          <rect x="15" y="19" width="3" height="10" rx="1" fill="#80CBC4"/>
          <rect x="22.5" y="19" width="3" height="10" rx="1" fill="#80CBC4"/>
          <rect x="30" y="19" width="3" height="10" rx="1" fill="#80CBC4"/>
          <rect x="12" y="30" width="24" height="2.5" rx="1" fill="#80CBC4"/>
          <text x="24" y="43" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="Arial">BANKEX</text>
        </svg>
      </div>
    );
  }
  
  // Generic fallback
  return (
    <div className={`${s} rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center shadow-sm`}>
      <span className={`text-white font-bold ${fs}`}>{symbol?.substring(0,6)}</span>
    </div>
  );
};

// Use the shared CommodityLogo from @/components/CommodityLogo.js
// (imported as CommodityLogoShared at top, aliased below for local usage)
const CommodityLogo = CommodityLogoShared;

// EXACT Original StoxCap Zigzag Chart - 3 peaks, 3 valleys pattern - Memoized for performance
const ZigzagChart = memo(({ isPositive, height = 40 }) => {
  const color = isPositive ? '#00b386' : '#eb5b3c';
  
  // Generate EXACT pattern from original: 3 peaks, 3 valleys
  // Sharp 45-degree angle lines connecting at vertices
  const path = useMemo(() => {
    const h = height;
    
    if (isPositive) {
      // Upward trending: 3 valleys, 3 peaks pattern (overall upward)
      // Pattern: down-up-down-up-down-up (ends higher)
      return `M 0 ${h * 0.55} ` +
             `L 17 ${h * 0.70} ` +  // Valley 1
             `L 33 ${h * 0.45} ` +  // Peak 1
             `L 50 ${h * 0.60} ` +  // Valley 2
             `L 67 ${h * 0.35} ` +  // Peak 2
             `L 83 ${h * 0.50} ` +  // Valley 3
             `L 100 ${h * 0.30}`;   // Peak 3 (ends high)
    } else {
      // Downward trending: 3 peaks, 3 valleys pattern (overall downward)
      // Pattern: up-down-up-down-up-down (ends lower)
      return `M 0 ${h * 0.45} ` +
             `L 17 ${h * 0.30} ` +  // Peak 1
             `L 33 ${h * 0.55} ` +  // Valley 1
             `L 50 ${h * 0.40} ` +  // Peak 2
             `L 67 ${h * 0.65} ` +  // Valley 2
             `L 83 ${h * 0.50} ` +  // Peak 3
             `L 100 ${h * 0.70}`;   // Valley 3 (ends low)
    }
  }, [isPositive, height]);

  return (
    <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
});

// AI Strategy Builder Card — Market/Limit modes, live tick premiums, multi-exchange (NFO/BFO/MCX/CDS)
const AIStrategyBuilder = ({ indexTicks }) => {
  const [strategyInput, setStrategyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [lots, setLots] = useState(1);
  const [executionMode, setExecutionMode] = useState('MARKET'); // MARKET or LIMIT
  const [limitPrices, setLimitPrices] = useState({}); // legIdx -> price
  const [livePremiums, setLivePremiums] = useState({}); // tradingsymbol -> premium
  const [tickRules, setTickRules] = useState(null); // tick_size etc from trading rules
  const [availableExpiries, setAvailableExpiries] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [lastStrategyText, setLastStrategyText] = useState('');
  const wsRef = useRef(null);
  const navigate = useNavigate();

  const quickStrategies = [
    { label: 'Long Straddle', text: 'Long straddle on NIFTY' },
    { label: 'Iron Condor', text: 'Iron condor on BANKNIFTY' },
    { label: 'Bull Call Spread', text: 'Bull call spread on NIFTY' },
    { label: 'Short Strangle', text: 'Short strangle on NIFTY' },
    { label: 'Bear Call Spread', text: 'Bear call spread on CRUDEOILM' },
    { label: 'Gold Straddle', text: 'Long straddle on GOLD' },
  ];

  // Subscribe to WebSocket ticks for live premium updates when strategy is built
  useEffect(() => {
    if (!strategy?.legs?.length) return;

    const ws = new WebSocket(
      `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/ticks`
    );
    wsRef.current = ws;

    ws.onopen = () => {
      const symbols = strategy.legs.map(leg => `${leg.exchange}:${leg.tradingsymbol}`);
      ws.send(JSON.stringify({ type: 'subscribe', symbols }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'tick' && data.tradingsymbol && data.last_price > 0) {
          setLivePremiums(prev => ({
            ...prev,
            [data.tradingsymbol]: data.last_price,
          }));
        }
        // Also handle batch tick messages
        if (data.type === 'ticks' && Array.isArray(data.data)) {
          setLivePremiums(prev => {
            const updated = { ...prev };
            for (const tick of data.data) {
              if (tick.tradingsymbol && tick.last_price > 0) {
                updated[tick.tradingsymbol] = tick.last_price;
              }
            }
            return updated;
          });
        }
      } catch {}
    };

    return () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [strategy?.legs]);

  // Fetch trading rules for the strategy's exchange when strategy changes
  useEffect(() => {
    if (!strategy?.exchange) return;
    axios.get(`${API}/trading/rules/${strategy.exchange}`)
      .then(res => setTickRules(res.data))
      .catch(() => {});
  }, [strategy?.exchange]);

  // Get live or static premium for a leg
  const getLegPremium = (leg, idx) => {
    if (executionMode === 'LIMIT') {
      return limitPrices[idx] ?? leg.premium;
    }
    return livePremiums[leg.tradingsymbol] || leg.premium;
  };

  const buildStrategy = async (expiryOverride = null) => {
    if (!strategyInput.trim()) {
      toast.error("Please enter a strategy description");
      return;
    }
    
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to use AI Strategy Builder");
      navigate("/login");
      return;
    }

    setLoading(true);
    if (!expiryOverride) {
      setStrategy(null);
      setAvailableExpiries([]);
      setSelectedExpiry('');
    }
    setLivePremiums({});
    setLimitPrices({});
    try {
      const payload = { 
        strategy_text: strategyInput,
        spot_price: indexTicks?.['NIFTY 50']?.last_price || indexTicks?.['NIFTY BANK']?.last_price || 0,
      };
      if (expiryOverride) {
        payload.expiry_override = expiryOverride;
      }
      const response = await axios.post(
        `${API}/ai/create-strategy`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const strat = response.data.strategy;
      setStrategy(strat);
      setLots(strat?.lots || 1);
      setLastStrategyText(strategyInput);
      // Set available expiries from response
      if (response.data.available_expiries?.length) {
        setAvailableExpiries(response.data.available_expiries);
      }
      setSelectedExpiry(strat?.expiry || '');
      // Initialize limit prices from built premiums
      const initLimits = {};
      strat.legs?.forEach((leg, idx) => { initLimits[idx] = leg.premium; });
      setLimitPrices(initLimits);
      toast.success(`${strat.strategy_name} — ${strat.exchange} | Expiry: ${strat.expiry}`);
    } catch (error) {
      console.error("Strategy error:", error);
      const errData = error.response?.data;
      const errorMsg = errData?.detail || "Failed to build strategy";
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      // If error response includes available_expiries, populate them
      if (errData?.available_expiries?.length) {
        setAvailableExpiries(errData.available_expiries);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle expiry change from dropdown — rebuild strategy with selected expiry
  const handleExpiryChange = (newExpiry) => {
    if (newExpiry === selectedExpiry || !newExpiry) return;
    setSelectedExpiry(newExpiry);
    setExpiryLoading(true);
    buildStrategy(newExpiry).finally(() => setExpiryLoading(false));
  };

  const executeStrategy = async () => {
    if (!strategy?.legs?.length) return;
    
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to execute strategy");
      navigate("/login");
      return;
    }

    // Validate all legs have real prices
    for (let i = 0; i < strategy.legs.length; i++) {
      const price = getLegPremium(strategy.legs[i], i);
      if (!price || price <= 0) {
        toast.error(`Cannot execute: price unavailable for leg ${i + 1}. Wait for market data.`);
        return;
      }
      // Validate tick size for limit orders
      if (executionMode === 'LIMIT' && tickRules?.default_tick_size) {
        const tick = tickRules.default_tick_size;
        const remainder = Math.round((price % tick) * 10000) / 10000;
        if (remainder > 0.0001 && Math.abs(remainder - tick) > 0.0001) {
          toast.error(`Leg ${i + 1}: Price ₹${price} is not a valid tick size multiple (tick: ${tick})`);
          return;
        }
      }
    }

    setExecuting(true);
    try {
      const orders = strategy.legs.map((leg, idx) => ({
        symbol: leg.tradingsymbol,
        underlying: leg.symbol,
        strike: leg.strike,
        option_type: leg.option_type,
        action: leg.action,
        quantity: leg.quantity * (lots / (strategy.lots || 1)),
        price: getLegPremium(leg, idx),
        order_type: executionMode,
        exchange: leg.exchange || strategy.exchange,
        expiry: leg.expiry,
        tradingsymbol: leg.tradingsymbol,
        source: "AI_STRATEGY_BUILDER",
        execution_mode: executionMode,
      }));

      const response = await axios.post(
        `${API}/fno/place-multi-order`,
        { 
          orders,
          strategy_name: strategy.strategy_name,
          total_lots: lots,
          source: "AI_STRATEGY_BUILDER",
          execution_mode: executionMode,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        toast.success(`Strategy executed (${executionMode})! ${orders.length} orders placed.`);
        setStrategy(null);
        setStrategyInput('');
      } else {
        toast.error(response.data?.detail || "Execution failed");
      }
    } catch (error) {
      console.error("Execute error:", error);
      const errMsg = error.response?.data?.detail || "Failed to execute strategy.";
      toast.error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    } finally {
      setExecuting(false);
    }
  };

  // Calculate metrics based on execution mode and live premiums
  const lotMultiplier = lots / (strategy?.lots || 1);
  const computedLegs = strategy?.legs?.map((leg, idx) => ({
    ...leg,
    livePremium: getLegPremium(leg, idx),
  })) || [];

  const totalCost = computedLegs.reduce((sum, leg) => {
    const premium = leg.livePremium || 0;
    const qty = leg.quantity * lotMultiplier;
    return sum + (leg.action === 'BUY' ? premium * qty : -premium * qty);
  }, 0);

  const maxLoss = strategy ? (typeof strategy.max_loss === 'number' ? strategy.max_loss * lotMultiplier : strategy.max_loss) : 0;
  const maxProfit = strategy ? (typeof strategy.max_profit === 'number' ? strategy.max_profit * lotMultiplier : strategy.max_profit) : 0;

  return (
    <Card className="p-5 rounded-2xl border border-purple-100 bg-gradient-to-b from-purple-50 to-purple-100/60" data-testid="ai-strategy-builder">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 text-lg">AI Strategy Builder</h3>
          <p className="text-sm text-gray-500">Any strategy in plain English — NFO, BFO, MCX, CDS — live market data</p>
        </div>
      </div>
      
      <div className="flex gap-3 mb-4">
        <Input
          value={strategyInput}
          onChange={(e) => setStrategyInput(e.target.value)}
          placeholder='e.g., "Bear call spread on crude oil mini" or "Iron condor on BANKNIFTY"'
          className="flex-1 bg-white h-11"
          onKeyDown={(e) => e.key === 'Enter' && buildStrategy()}
          disabled={loading}
          data-testid="strategy-input"
        />
        <Button 
          onClick={() => buildStrategy()}
          disabled={loading}
          data-testid="build-strategy-btn"
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 h-11"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {loading ? "Building..." : "Build Strategy"}
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500">Quick:</span>
        {quickStrategies.map((strat, idx) => (
          <button
            key={idx}
            onClick={() => setStrategyInput(strat.text)}
            disabled={loading}
            data-testid={`quick-strategy-chip-${idx}`}
            className="px-3 py-1.5 text-sm bg-white hover:bg-gray-50 rounded-full text-gray-600 transition-colors border border-gray-200 disabled:opacity-50"
          >
            {strat.label}
          </button>
        ))}
      </div>

      {/* Strategy Result */}
      {strategy && (
        <div className="mt-4 p-4 bg-white rounded-xl border border-purple-200" data-testid="strategy-result">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h4 className="font-semibold text-purple-700">{strategy.strategy_name}</h4>
              <p className="text-xs text-gray-400">
                Spot: ₹{strategy.spot_price?.toLocaleString('en-IN')} | {strategy.exchange}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Expiry Picker Dropdown */}
              {availableExpiries.length > 0 && (
                <div className="relative" data-testid="expiry-picker">
                  <select
                    value={selectedExpiry}
                    onChange={(e) => handleExpiryChange(e.target.value)}
                    disabled={expiryLoading || loading}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-purple-400 disabled:opacity-50 appearance-none pr-6"
                    style={{ minWidth: '100px' }}
                    data-testid="expiry-select"
                  >
                    {availableExpiries.map((exp) => {
                      const d = new Date(exp + 'T00:00:00');
                      const label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                      return (
                        <option key={exp} value={exp}>{label}</option>
                      );
                    })}
                  </select>
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">
                    {expiryLoading ? '...' : '▼'}
                  </span>
                </div>
              )}
              {/* Market/Limit Toggle */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden text-xs" data-testid="execution-mode-toggle">
                <button
                  onClick={() => setExecutionMode('MARKET')}
                  className={`px-3 py-1.5 font-medium transition-colors ${executionMode === 'MARKET' ? 'bg-purple-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  data-testid="mode-market-btn"
                >
                  Market
                </button>
                <button
                  onClick={() => setExecutionMode('LIMIT')}
                  className={`px-3 py-1.5 font-medium transition-colors ${executionMode === 'LIMIT' ? 'bg-purple-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  data-testid="mode-limit-btn"
                >
                  Limit
                </button>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                strategy.strategy_type === 'bullish' ? 'bg-green-100 text-green-700' :
                strategy.strategy_type === 'bearish' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {strategy.strategy_type?.toUpperCase() || 'NEUTRAL'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="text-center p-2 bg-gray-50 rounded" data-testid="strategy-total-cost">
              <p className="text-xs text-gray-500">Total Cost</p>
              <p className="font-semibold text-gray-800">₹{Math.abs(totalCost).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
            </div>
            <div className="text-center p-2 bg-green-50 rounded" data-testid="strategy-max-profit">
              <p className="text-xs text-gray-500">Max Profit</p>
              <p className="font-semibold text-green-600">{maxProfit === 'Unlimited' ? 'Unlimited' : `₹${(typeof maxProfit === 'number' ? maxProfit : 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}</p>
            </div>
            <div className="text-center p-2 bg-red-50 rounded" data-testid="strategy-max-loss">
              <p className="text-xs text-gray-500">Max Loss</p>
              <p className="font-semibold text-red-600">{maxLoss === 'Unlimited' ? 'Unlimited' : `₹${(typeof maxLoss === 'number' ? Math.abs(maxLoss) : 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}</p>
            </div>
            <div className="text-center p-2 bg-blue-50 rounded" data-testid="strategy-lot-size">
              <p className="text-xs text-gray-500">Lot Size</p>
              <p className="font-semibold text-blue-600">{strategy.lot_size}</p>
            </div>
          </div>

          {/* Strategy Legs — with live premiums and limit price editing */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Strategy Legs:</p>
              {executionMode === 'MARKET' && Object.keys(livePremiums).length > 0 && (
                <span className="text-[10px] text-green-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Live ticks
                </span>
              )}
            </div>
            {computedLegs.map((leg, idx) => {
              const isLive = livePremiums[leg.tradingsymbol] && livePremiums[leg.tradingsymbol] !== leg.premium;
              return (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm" data-testid={`strategy-leg-${idx}`}>
                  <span className={`font-medium ${leg.action === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>
                    {leg.action} {leg.symbol} {leg.strike} {leg.option_type}
                  </span>
                  <div className="flex items-center gap-3">
                    {executionMode === 'LIMIT' ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">₹</span>
                        <input
                          type="number"
                          step={tickRules?.default_tick_size || 0.05}
                          value={limitPrices[idx] ?? leg.premium}
                          onChange={(e) => setLimitPrices(prev => ({...prev, [idx]: parseFloat(e.target.value) || 0}))}
                          className="w-20 text-right text-sm border border-gray-200 rounded px-2 py-0.5 font-mono"
                          data-testid={`leg-limit-price-${idx}`}
                        />
                      </div>
                    ) : (
                      <span className={`font-mono text-sm ${isLive ? 'text-green-700 font-semibold' : 'text-gray-600'}`}>
                        ₹{leg.livePremium?.toFixed(2)}
                        {isLive && <RefreshCw className="w-3 h-3 inline ml-1 text-green-500" />}
                      </span>
                    )}
                    <span className="text-gray-500 text-xs">
                      Qty: {Math.round(leg.quantity * lotMultiplier)}
                      {leg.oi > 0 && <span className="text-gray-400 ml-1">OI: {(leg.oi / 1000).toFixed(0)}K</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {strategy.market_view && (
            <p className="mb-4 text-sm text-gray-500 italic">{strategy.market_view}</p>
          )}

          {/* Lot Selector and Execute */}
          <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Lots:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setLots(Math.max(1, lots - 1))} data-testid="lots-decrease-btn" className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium">-</button>
                <input type="number" value={lots} onChange={(e) => setLots(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 text-center py-1.5 border-x border-gray-200 font-semibold" min="1" />
                <button onClick={() => setLots(lots + 1)} data-testid="lots-increase-btn" className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium">+</button>
              </div>
            </div>

            <div className="flex-1 text-right">
              <p className="text-xs text-gray-500 mb-1">Total ({executionMode})</p>
              <p className="text-lg font-bold text-gray-800">₹{Math.abs(totalCost).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
            </div>

            <Button 
              onClick={executeStrategy}
              disabled={executing}
              data-testid="execute-strategy-btn"
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 h-11 font-semibold"
            >
              {executing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Executing...</>
              ) : (
                <><TrendingUp className="w-4 h-4 mr-2" />{executionMode === 'MARKET' ? 'Execute at Market' : 'Execute Limit'}</>
              )}
            </Button>
          </div>

          <p className="text-[10px] text-gray-400 mt-2 text-center">
            All premiums from live market data. {executionMode === 'MARKET' ? 'Prices update via WebSocket.' : `Tick size: ${tickRules?.default_tick_size || '—'}`} {availableExpiries.length > 1 ? `${availableExpiries.length} expiries available.` : ''}
          </p>
        </div>
      )}
    </Card>
  );
};

// Chart Icon - Purple style for opening charts
const ChartIcon = () => (
  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M1 7C2.5 6 3 4 4 5.5C5 7 5.5 11 6 12C6.5 13 7 12 7.5 10C8 8 8.5 2 9 1C9.5 0 10 1 10.5 3.5C11 6 11.5 7 12 7C12.5 7 13 6 14 6C15 6 16 7 17 7" 
      stroke="#8B5CF6"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Top Traded Card - Original style with LIVE data - Memoized for performance
const TopTradedCard = memo(({ name, ticker, price, changePercent, change, onClick, liveData, isLive, marketOpen }) => {
  // Use live data if available — only trust live change when it's non-zero (market open)
  const displayPrice = liveData?.last_price || price;
  const liveChange = liveData?.change_percent ?? liveData?.change;
  const displayChange = (liveChange && liveChange !== 0) ? liveChange : changePercent;
  const displayRupeeChange = liveData?.change ?? change ?? (displayPrice * displayChange / 100);
  const isPositive = displayChange >= 0;
  const [isHovered, setIsHovered] = useState(false);
  const [flash, setFlash] = useState(false);
  const prevPriceRef = useRef(displayPrice);
  
  // Flash effect on price update - using ref-based approach to avoid lint warning
  useEffect(() => {
    if (liveData?.last_price && liveData.last_price !== prevPriceRef.current) {
      prevPriceRef.current = liveData.last_price;
      // Schedule flash effect in next microtask
      Promise.resolve().then(() => {
        setFlash(true);
        setTimeout(() => setFlash(false), 300);
      });
    }
  }, [liveData?.last_price]);
  
  // Open TradingView chart in a new tab with canonical symbol mapping
  const handleChartClick = useCallback((e) => {
    e.stopPropagation();
    openChartSafe(ticker);
  }, [ticker]);
  
  return (
    <Card 
      className={`p-4 rounded-xl border hover:shadow-md hover:border-gray-200 transition-all bg-white relative cursor-pointer ${
        flash ? 'border-green-300 ring-2 ring-green-200' : 'border-gray-100'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      data-testid={`top-traded-card-${ticker}`}
    >
      {/* Chart Icon - Purple icon to open chart, shows on hover */}
      <div 
        className={`absolute top-3 right-3 p-1.5 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all duration-200 cursor-pointer z-10 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleChartClick}
        data-testid={`chart-icon-${ticker}`}
      >
        <ChartIcon />
      </div>
      
      <div className="mb-2 pr-8">
        <p className="font-semibold text-gray-900 truncate">{name}</p>
        <p className="text-xs text-gray-500 truncate">{ticker}</p>
      </div>
      
      <div className="h-12 my-3">
        <ZigzagChart isPositive={isPositive} height={48} />
      </div>
      
      <div className="flex items-end justify-between">
        <p className={`text-lg font-bold transition-colors duration-200 ${
          flash ? (isPositive ? 'text-green-600' : 'text-red-600') : 'text-gray-900'
        }`}>
          ₹{displayPrice?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        <p className={`text-sm font-medium ${isPositive ? 'text-[#00b386]' : 'text-[#eb5b3c]'}`}>
          ₹{Math.abs(displayRupeeChange)?.toFixed(2)} ({isPositive ? '+' : ''}{displayChange?.toFixed(2)}%)
        </p>
      </div>
    </Card>
  );
});

// F&O Stock Card - Original style with LIVE data - Memoized for performance
const FOStockCard = memo(({ name, ticker, price, changePercent, change, onClick, liveData, isLive, marketOpen, onAlert, onWatchlistToggle, isInWatchlist }) => {
  // Use live data if available — only trust live change when non-zero (market open)
  const displayPrice = liveData?.last_price || price;
  const liveChange = liveData?.change_percent ?? liveData?.change;
  const displayChange = (liveChange && liveChange !== 0) ? liveChange : changePercent;
  const displayRupeeChange = liveData?.change ?? change ?? (displayPrice * displayChange / 100);
  const isPositive = displayChange >= 0;
  const [flash, setFlash] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const prevPriceRef = useRef(displayPrice);
  
  // Flash effect on price update - using ref-based approach to avoid lint warning
  useEffect(() => {
    if (liveData?.last_price && liveData.last_price !== prevPriceRef.current) {
      prevPriceRef.current = liveData.last_price;
      Promise.resolve().then(() => {
        setFlash(true);
        setTimeout(() => setFlash(false), 300);
      });
    }
  }, [liveData?.last_price]);

  // Open chart — TradingView for known symbols, in-app chart as fallback
  const handleChartClick = useCallback((e) => {
    e.stopPropagation();
    openChartSafe(ticker);
  }, [ticker]);
  
  return (
    <Card 
      className={`p-4 rounded-xl border hover:shadow-md hover:border-gray-200 transition-all bg-white cursor-pointer relative ${
        flash ? 'border-green-300 ring-2 ring-green-200' : 'border-gray-100'
      }`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`fo-stock-card-${ticker}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-sm truncate">{name}</p>
          <p className="text-xs text-gray-500 truncate">{ticker}</p>
        </div>
        <div className="flex items-center gap-1">
          {/* Alert icon - Shows on hover */}
          <div 
            className={`p-1.5 bg-orange-50 hover:bg-orange-100 rounded-lg transition-all duration-200 cursor-pointer ${isHovered ? 'opacity-100' : 'opacity-0'}`}
            onClick={(e) => { e.stopPropagation(); onAlert?.(ticker, displayPrice); }}
            data-testid={`fo-alert-icon-${ticker}`}
            title="Create Alert"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-600"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </div>
          {/* Watchlist star - Shows on hover */}
          <div 
            className={`p-1.5 rounded-lg transition-all duration-200 cursor-pointer ${isInWatchlist ? 'bg-yellow-100 opacity-100' : isHovered ? 'bg-gray-50 hover:bg-yellow-50 opacity-100' : 'opacity-0'}`}
            onClick={(e) => { e.stopPropagation(); onWatchlistToggle?.(ticker, name); }}
            data-testid={`fo-watchlist-icon-${ticker}`}
            title={isInWatchlist ? "Remove from Watchlist" : "Add to Watchlist"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={isInWatchlist ? "#eab308" : "none"} stroke={isInWatchlist ? "#eab308" : "currentColor"} strokeWidth="2" className={isInWatchlist ? "text-yellow-500" : "text-gray-500"}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </div>
          {/* Purple Chart Icon - Shows on hover */}
          <div 
            className={`p-1.5 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all duration-200 cursor-pointer ${isHovered ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleChartClick}
            data-testid={`fo-chart-icon-${ticker}`}
            title="Open Chart"
          >
            <ChartIcon />
          </div>
        </div>
      </div>
      
      <div className="h-10 my-3">
        <ZigzagChart isPositive={isPositive} height={40} />
      </div>
      
      <div className="flex items-end justify-between">
        <p className={`text-base font-bold transition-colors duration-200 ${
          flash ? (isPositive ? 'text-green-600' : 'text-red-600') : 'text-gray-900'
        }`}>
          ₹{displayPrice?.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
        </p>
        <p className={`text-sm font-medium ${isPositive ? 'text-[#00b386]' : 'text-[#eb5b3c]'}`}>
          ₹{Math.abs(displayRupeeChange)?.toFixed(2)} ({isPositive ? '+' : ''}{displayChange?.toFixed(2)}%)
        </p>
      </div>
    </Card>
  );
});

const INDEX_DISPLAY_NAMES_EP = {
  'NIFTY': 'Nifty 50',
  'BANKNIFTY': 'Bank Nifty',
  'FINNIFTY': 'Fin Nifty',
  'MIDCPNIFTY': 'Mid Cap Nifty',
  'SENSEX': 'Sensex',
  'BANKEX': 'Bankex',
  'SENSEX50': 'Sensex 50',
};

export default function FOExplorePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('explore');
  const [stockFilter, setStockFilter] = useState('gainers');
  const [hoveredFoRow, setHoveredFoRow] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [topTraded, setTopTraded] = useState([]);
  const [foStocks, setFoStocks] = useState([]);
  const [stockFutures, setStockFutures] = useState([]);
  const [indexFutures, setIndexFutures] = useState([]);
  const [commodityFutures, setCommodityFutures] = useState([]);
  
  // Positions summary for sidebar box
  const [sidebarPositions, setSidebarPositions] = useState([]);
  const [sidebarPnl, setSidebarPnl] = useState(0);

  // ── Alert + Watchlist state ─────────────────────────────────
  const allStockSymbols = useMemo(() => foStocks.map(s => s.ticker).filter(Boolean), [foStocks]);
  const { results: watchlistMap, toggle: toggleWatchlist } = useWatchlistBatch(allStockSymbols);
  useCriticalLogos(allStockSymbols, { critical: true });
  const [showFoAlertDialog, setShowFoAlertDialog] = useState(false);
  const [foAlertSymbol, setFoAlertSymbol] = useState("");
  const [foAlertPrice, setFoAlertPrice] = useState("");
  const [foAlertType, setFoAlertType] = useState("above");
  const [foAlertNote, setFoAlertNote] = useState("");
  const [foAlertLoading, setFoAlertLoading] = useState(false);

  const handleFoAlert = useCallback((ticker, price) => {
    setFoAlertSymbol(ticker);
    setFoAlertPrice((price || "").toString());
    setShowFoAlertDialog(true);
  }, []);

  const handleFoAlertSubmit = async () => {
    const token = localStorage.getItem("token");
    if (!token) { toast.error("Please login first"); return; }
    if (!foAlertPrice) { toast.error("Enter a target price"); return; }
    setFoAlertLoading(true);
    try {
      const res = await fetch(`${API}/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          symbol: foAlertSymbol,
          alert_type: foAlertType,
          target_price: parseFloat(foAlertPrice),
          note: foAlertNote || `${foAlertSymbol} ${foAlertType} ₹${foAlertPrice}`,
          exchange: "NSE",
          instrument_type: "equity",
        })
      });
      if (res.ok) {
        toast.success(`Alert: ${foAlertSymbol} ${foAlertType} ₹${foAlertPrice}`);
        setShowFoAlertDialog(false);
        setFoAlertPrice(""); setFoAlertNote("");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create alert");
      }
    } catch { toast.error("Failed to create alert"); }
    finally { setFoAlertLoading(false); }
  };

  const handleFoWatchlistToggle = useCallback(async (ticker, name) => {
    const result = await toggleWatchlist(ticker, { name: name || ticker, exchange: "NSE", segment: "equity", asset_class: "fo" });
    if (result) {
      toast.success(result.in_watchlist ? `${ticker} added to watchlist` : `${ticker} removed from watchlist`);
    }
  }, [toggleWatchlist]);
  
  // Real-time data for indices
  const indexSymbols = useMemo(() => 
    ['NIFTY 50', 'NIFTY BANK', 'SENSEX', 'NIFTY MID SELECT', 'NIFTY FIN SERVICE'], 
    []
  );
  const { ticks: indexTicks, isConnected: isIndexLive, marketOpen: foMarketOpen } = useRealtimeData(indexSymbols, 'NSE');
  
  // Real-time data for F&O stocks
  // Subscribe to ALL foStocks tickers (gainers + losers) — no slice cap
  const stockSymbols = useMemo(() => 
    foStocks.map(s => s.ticker).filter(Boolean),
    [foStocks]
  );
  const { ticks: stockTicks, isConnected: isStockLive } = useRealtimeData(stockSymbols, 'NSE');
  
  // Real-time data for stock futures (use NFO tradingsymbols for accurate futures prices)
  const futuresNfoSymbols = useMemo(() => 
    stockFutures.map(f => f.nfoTs).filter(Boolean),
    [stockFutures]
  );
  const { ticks: futuresNfoTicks, isConnected: isFuturesLive } = useRealtimeData(futuresNfoSymbols, 'NFO');
  // Build a map: ticker -> tick data
  const futuresTicks = useMemo(() => {
    const map = {};
    stockFutures.forEach(f => {
      if (f.nfoTs && futuresNfoTicks[f.nfoTs]) {
        map[f.ticker] = futuresNfoTicks[f.nfoTs];
      }
    });
    return map;
  }, [stockFutures, futuresNfoTicks]);

  // Real-time data for index futures — split NFO and BFO subscriptions
  const idxFutNfoSymbols = useMemo(() => 
    indexFutures.filter(f => f.nfoTs && f.nfoExchange === 'NFO').map(f => f.nfoTs),
    [indexFutures]
  );
  const { ticks: idxFutNfoTicks } = useRealtimeData(idxFutNfoSymbols, 'NFO');
  const idxFutBfoSymbols = useMemo(() => 
    indexFutures.filter(f => f.nfoTs && f.nfoExchange === 'BFO').map(f => f.nfoTs),
    [indexFutures]
  );
  const { ticks: idxFutBfoTicks } = useRealtimeData(idxFutBfoSymbols, 'BFO');
  // Build a map: ticker -> tick data
  const indexFutTicks = useMemo(() => {
    const map = {};
    indexFutures.forEach(f => {
      if (f.nfoTs) {
        const tickSource = f.nfoExchange === 'BFO' ? idxFutBfoTicks : idxFutNfoTicks;
        if (tickSource[f.nfoTs]) {
          map[f.ticker] = tickSource[f.nfoTs];
        }
      }
    });
    return map;
  }, [indexFutures, idxFutNfoTicks, idxFutBfoTicks]);

  // Real-time data for commodity futures (MCX tradingsymbols)
  const commMcxSymbols = useMemo(() => 
    commodityFutures.map(c => c.mcxTs).filter(Boolean),
    [commodityFutures]
  );
  const { ticks: commMcxTicks } = useRealtimeData(commMcxSymbols, 'MCX');
  // Build a map: symbol -> tick data
  const commTicks = useMemo(() => {
    const map = {};
    commodityFutures.forEach(c => {
      if (c.mcxTs && commMcxTicks[c.mcxTs]) {
        map[c.symbol] = commMcxTicks[c.mcxTs];
      }
    });
    return map;
  }, [commodityFutures, commMcxTicks]);

  // Real-time data for F&O position symbols — grouped by exchange
  const positionMcxSymbols = useMemo(() => 
    sidebarPositions.filter(p => p.segment === 'commodity').map(p => p.symbol).filter(Boolean),
    [sidebarPositions]
  );
  const { ticks: posMcxTicks } = useRealtimeData(positionMcxSymbols, 'MCX');
  
  const positionCdsSymbols = useMemo(() => 
    sidebarPositions.filter(p => p.segment === 'forex').map(p => p.symbol).filter(Boolean),
    [sidebarPositions]
  );
  const { ticks: posCdsTicks } = useRealtimeData(positionCdsSymbols, 'CDS');
  
  const positionNfoSymbols = useMemo(() => 
    sidebarPositions.filter(p => p.segment === 'fo' || (!p.segment)).map(p => p.symbol).filter(Boolean),
    [sidebarPositions]
  );
  const { ticks: posNfoTicks } = useRealtimeData(positionNfoSymbols, 'NFO');
  
  // Merge all position ticks into one map
  const positionTicks = useMemo(() => ({
    ...posNfoTicks,
    ...posMcxTicks,
    ...posCdsTicks,
  }), [posNfoTicks, posMcxTicks, posCdsTicks]);

  // Recompute positions P&L from live ticks
  const livePositions = useMemo(() => {
    if (!sidebarPositions.length) return { positions: [], totalPnl: 0 };
    let totalPnl = 0;
    const positions = sidebarPositions.map(p => {
      const tick = positionTicks[p.symbol];
      const livePrice = tick?.last_price || p.ltp || p.last_price || 0;
      const qty = p.quantity || 0;
      const avgPrice = p.average_price || p.avg_price || 0;
      const pnl = qty * (livePrice - avgPrice);
      totalPnl += pnl;
      return { ...p, ltp: livePrice, pnl: Math.round(pnl * 100) / 100 };
    });
    return { positions, totalPnl: Math.round(totalPnl * 100) / 100 };
  }, [sidebarPositions, positionTicks]);

  // ── P&L flash animation tracking ──
  const prevPnlRef = useRef({});
  const [pnlFlash, setPnlFlash] = useState({}); // { [symbol]: 'up' | 'down' }
  const prevTotalPnlRef = useRef(0);
  const [totalPnlFlash, setTotalPnlFlash] = useState(null);

  useEffect(() => {
    const flashes = {};
    let hasFlash = false;
    livePositions.positions.forEach(p => {
      const prev = prevPnlRef.current[p.symbol];
      if (prev !== undefined && prev !== p.pnl) {
        flashes[p.symbol] = p.pnl > prev ? 'up' : 'down';
        hasFlash = true;
      }
      prevPnlRef.current[p.symbol] = p.pnl;
    });
    // Total P&L flash
    if (prevTotalPnlRef.current !== 0 && prevTotalPnlRef.current !== livePositions.totalPnl) {
      setTotalPnlFlash(livePositions.totalPnl > prevTotalPnlRef.current ? 'up' : 'down');
      setTimeout(() => setTotalPnlFlash(null), 600);
    }
    prevTotalPnlRef.current = livePositions.totalPnl;
    if (hasFlash) {
      setPnlFlash(flashes);
      setTimeout(() => setPnlFlash({}), 600);
    }
  }, [livePositions]);

  // ── Retry state ──
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const [fetchError, setFetchError] = useState(false);
  const loadingTimeoutRef = useRef(null);

  const fetchData = useCallback(async (isRetry = false) => {
    if (!isRetry) setFetchError(false);
    try {
      // BFF: Single API call replaces 4 parallel calls for faster Phase-1 rendering
      const snapshotRes = await axios.get(`${API}/fo/explore-snapshot`, { timeout: 20000 });
      const snap = snapshotRes.data || {};

      // Reshape — always create result objects even for partial data so sections render
      const indicesArr = snap.indices || [];
      const gainersArr = snap.movers?.gainers || [];
      const losersArr = snap.movers?.losers || [];
      const stockFutArr = snap.stock_futures || [];
      const indexFutArr = snap.index_futures || [];
      const mcxFutArr = snap.mcx_futures || [];

      const anyData = !!(indicesArr.length || stockFutArr.length || indexFutArr.length);

      // If no critical data and we have retries left, auto-retry with backoff
      if (!anyData && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const backoff = retryCountRef.current * 2000; // 2s, 4s
        console.warn(`FOExplorePage: critical data empty, auto-retry ${retryCountRef.current}/${MAX_RETRIES} in ${backoff}ms`);
        setTimeout(() => fetchData(true), backoff);
        return; // Don't set loading=false yet
      }

      if (!anyData && retryCountRef.current >= MAX_RETRIES) {
        console.error("FOExplorePage: all retries exhausted, no data available");
        setFetchError(true);
      }

      // Reset retry counter on success
      if (anyData) {
        retryCountRef.current = 0;
        setFetchError(false);
      }

      // D1: Top traded indices — always 5 cards, with fallback from index futures
      if (indicesArr.length) {
        const indexData = indicesArr.map(f => ({
          name: INDEX_DISPLAY_NAMES_EP[f.symbol] || f.name,
          symbol: f.symbol,
          price: f.price,
          changePercent: f.change_percent,
          change: f.change || 0,
        }));
        setTopTraded(indexData.slice(0, 5));
      } else if (indexFutArr.length) {
        // Fallback: Use index futures list
        const indexData = indexFutArr.map(f => ({
          name: INDEX_DISPLAY_NAMES_EP[f.symbol] || f.name || f.symbol,
          symbol: f.symbol,
          price: f.price,
          changePercent: f.change_percent,
          change: f.change || 0,
        }));
        setTopTraded(indexData.slice(0, 5));
      }

      // D3: F&O stocks from movers endpoint
      if (gainersArr.length || losersArr.length) {
        const gainersData = gainersArr.slice(0, 12).map(f => ({
          name: f.name || f.symbol,
          ticker: f.symbol,
          price: f.price,
          changePercent: f.change_percent,
          change: f.change || 0,
        }));
        const losersData = losersArr.slice(0, 12).map(f => ({
          name: f.name || f.symbol,
          ticker: f.symbol,
          price: f.price,
          changePercent: f.change_percent,
          change: f.change || 0,
        }));
        const combined = [...gainersData, ...losersData];
        const uniqueMap = new Map();
        combined.forEach(s => { if (!uniqueMap.has(s.ticker)) uniqueMap.set(s.ticker, s); });
        setFoStocks(Array.from(uniqueMap.values()));
      } else if (stockFutArr.length) {
        // Fallback: use stock futures as F&O stocks
        const stockData = stockFutArr.slice(0, 12).map(f => ({
          name: f.name || f.symbol,
          ticker: f.symbol,
          price: f.price,
          changePercent: f.change_percent,
          change: f.change || 0,
        }));
        setFoStocks(stockData.length > 0 ? stockData : []);
      }

      // Stock futures data — sort by contracts_traded
      const allStockFutures = stockFutArr.filter(f => f.price > 0);
      allStockFutures.sort(sortByContracts);
      const futuresData = allStockFutures.slice(0, 20).map(f => ({
        symbol: `${f.symbol}FUT`,
        ticker: f.symbol,
        name: f.name || f.symbol,
        nfoTs: f.nfo_tradingsymbol || null,
        nfoExpiry: f.nfo_expiry || null,
        expiry: f.nfo_expiry ? formatExpiryFromISO(f.nfo_expiry) : getNextMonthlyExpiry(),
        ltp: f.price,
        change: f.change_percent,
        rupeeChange: f.change || 0,
        oi: f.oi ? `${(f.oi / 100000).toFixed(1)}L` : "—",
        volume: formatVolume(f.volume),
        contracts_traded: f.contracts_traded || 0,
        turnover: f.turnover || 0,
        lot_size: f.lot_size || 1,
      }));
      setStockFutures(futuresData.length > 0 ? futuresData : []);

      // Index futures data — sort by contracts_traded for real ranking
      const allIdxFut = indexFutArr.filter(f => f.price > 0);
      allIdxFut.sort(sortByContracts);
      const idxFuturesData = allIdxFut.map(f => ({
        symbol: `${f.symbol}FUT`,
        ticker: f.symbol,
        nfoTs: f.nfo_tradingsymbol || null,
        nfoExchange: f.nfo_exchange || 'NFO',
        nfoExpiry: f.nfo_expiry || null,
        expiry: f.nfo_expiry ? formatExpiryFromISO(f.nfo_expiry) : getNextMonthlyExpiry(),
        ltp: f.price,
        change: f.change_percent || f.changePercent || 0,
        rupeeChange: f.change || 0,
        oi: f.oi ? `${(f.oi / 100000).toFixed(1)}L` : "—",
        volume: formatVolume(f.volume),
        contracts_traded: f.contracts_traded || 0,
        turnover: f.turnover || 0,
        lot_size: f.lot_size || 1,
      }));
      setIndexFutures(idxFuturesData);

      // Process MCX commodity futures
      const mcxFromFutList = mcxFutArr.filter(c => c.price > 0);
      mcxFromFutList.sort(sortByContracts);

      const commData = mcxFromFutList.slice(0, 12).map(c => {
        return {
          symbol: c.symbol,
          name: c.name,
          category: c.category || c.sector || 'Commodity',
          ltp: c.price,
          change: c.change_percent || 0,
          rupeeChange: c.change || 0,
          lot_size: c.lot_size,
          unit: c.unit || '',
          exchange: c.nfo_exchange || c.exchange || 'MCX',
          volume: c.volume,
          mcxTs: c.nfo_tradingsymbol || null,
          expiry: c.nfo_expiry ? formatExpiryFromISO(c.nfo_expiry) : '',
          contracts_traded: c.contracts_traded || 0,
          turnover: c.turnover || 0,
        };
      });
      setCommodityFutures(commData);

    } catch (error) {
      console.error("Error fetching F&O data:", error);
      // Auto-retry on catastrophic failure
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const backoff = retryCountRef.current * 2000;
        console.warn(`FOExplorePage: fetch failed, auto-retry ${retryCountRef.current}/${MAX_RETRIES} in ${backoff}ms`);
        setTimeout(() => fetchData(true), backoff);
        return;
      }
      setFetchError(true);
    } finally {
      setLoading(false);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Safety timeout: never get stuck in loading state for more than 15 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          console.warn("FOExplorePage: loading timeout reached (15s), forcing render");
          setFetchError(true);
          return false;
        }
        return prev;
      });
    }, 15000);
    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, [fetchData]);

  // Fetch positions summary for sidebar box — all derivative positions
  const fetchSidebarPositions = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await axios.get(`${API}/positions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const positions = res.data.positions || [];
      setSidebarPnl(res.data.summary?.total_unrealized_pnl || res.data.total_pnl || 0);

      // Resolve F&O symbols to human-readable names
      const foSymbols = positions.map(p => p.symbol).filter(Boolean);
      if (foSymbols.length > 0) {
        try {
          const namesRes = await axios.get(
            `${API}/fo/resolve-names?symbols=${foSymbols.join(',')}`
          );
          const nameMap = namesRes.data?.names || {};
          const enriched = positions.map(p => ({
            ...p,
            display_name: nameMap[p.symbol] || nameMap[p.tradingsymbol] || p.symbol,
          }));
          setSidebarPositions(enriched);
        } catch (e) {
          console.warn("[FOExplorePage] resolve-names failed:", e?.message, e?.response?.status);
          setSidebarPositions(positions);
        }
      } else {
        setSidebarPositions(positions);
      }
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchSidebarPositions();
    // Load-once REST — no polling; WebSocket handles live P&L
  }, [fetchSidebarPositions]);

  // Tick-driven gainers/losers: use live change_percent when available
  // On bearish days, "Top gainers" shows relative gainers (least-losing) to avoid empty cards
  const filteredStocks = useMemo(() => {
    const enriched = foStocks.map(s => {
      const tick = stockTicks[s.ticker];
      const livePct = tick?.change_percent;
      const pct = (livePct != null && livePct !== 0) ? livePct : (s.changePercent || 0);
      return { ...s, _livePct: pct };
    });
    if (stockFilter === 'gainers') {
      // Sort by highest change — show top 6 regardless of sign
      // On down days this shows "least losing" which is the correct relative-gainer behavior
      const sorted = [...enriched].sort((a, b) => (b._livePct) - (a._livePct));
      return sorted.slice(0, 6);
    } else {
      // Sort by lowest change — show bottom 6 regardless of sign
      const sorted = [...enriched].sort((a, b) => (a._livePct) - (b._livePct));
      return sorted.slice(0, 6);
    }
  }, [foStocks, stockTicks, stockFilter]);

  // ── Tick-driven re-ranking: top traded stock futures ──
  const sortedStockFutures = useMemo(() => reRankByTicks(stockFutures, futuresTicks, 'ticker'), [stockFutures, futuresTicks]);

  // ── Tick-driven re-ranking: top traded index futures ──
  const sortedIndexFutures = useMemo(() => reRankByTicks(indexFutures, indexFutTicks, 'ticker'), [indexFutures, indexFutTicks]);

  // ── Tick-driven re-ranking: top traded commodity futures ──
  const sortedCommodityFutures = useMemo(() => reRankByTicks(commodityFutures, commTicks, 'symbol'), [commodityFutures, commTicks]);

  if (loading) {
    return (
      <div className="w-full pb-8">
        {/* Skeleton Loader for F&O page */}
        <div className="space-y-6">
          {/* Tabs skeleton */}
          <div className="flex gap-8 border-b border-gray-200 pb-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
          
          {/* AI Strategy Builder skeleton */}
          <div className="bg-purple-50 rounded-2xl p-5 border border-purple-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-200 rounded-xl animate-pulse"></div>
              <div>
                <div className="h-5 w-40 bg-purple-200 rounded animate-pulse mb-2"></div>
                <div className="h-4 w-64 bg-purple-200 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1 h-11 bg-white rounded-lg animate-pulse"></div>
              <div className="h-11 w-36 bg-purple-300 rounded-lg animate-pulse"></div>
            </div>
          </div>
          
          {/* Top traded skeleton */}
          <div className="space-y-3">
            <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-white p-4 rounded-xl shadow-sm">
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-6 w-24 bg-gray-200 rounded animate-pulse mb-1"></div>
                  <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* F&O Stocks skeleton */}
          <div className="space-y-3">
            <div className="h-6 w-28 bg-gray-200 rounded animate-pulse"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white p-3 rounded-xl shadow-sm">
                  <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-5 w-20 bg-gray-200 rounded animate-pulse mb-1"></div>
                  <div className="h-4 w-14 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <SEOHead {...PageSEO.fno} />
    <div className="w-full pb-8" data-testid="fo-explore-page">
      {/* WS Connection Banner */}
      <ConnectionBanner isConnected={isIndexLive || isStockLive || isFuturesLive} streamStatus={(isIndexLive || isStockLive || isFuturesLive) ? 'live' : 'reconnecting'} />
      {/* NOTE: Layout sub-nav handles tab navigation to /fo/holdings, /fo/positions, etc. */}

      <div className="space-y-8">
        {/* AI Strategy Builder */}
          <AIStrategyBuilder indexTicks={indexTicks} />

          {/* Top Traded - 5 cards with LIVE data */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Top traded</h2>
              </div>
              <button
                onClick={() => navigate('/fo/top-traded')}
                className="text-sm font-medium text-[#00b386] hover:underline"
                data-testid="top-traded-see-more"
              >
                See more
              </button>
            </div>

            {topTraded.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {topTraded.map((item, idx) => {
                // Map to real-time tick symbol
                const tickSymbolMap = {
                  'NIFTY50': 'NIFTY 50',
                  'NIFTY': 'NIFTY 50',
                  'SENSEX': 'SENSEX',
                  'BANKNIFTY': 'NIFTY BANK',
                  'MIDCPNIFTY': 'NIFTY MID SELECT',
                  'FINNIFTY': 'NIFTY FIN SERVICE',
                };
                const tickSymbol = tickSymbolMap[item.symbol];
                const liveData = tickSymbol ? indexTicks[tickSymbol] : null;
                
                return (
                  <TopTradedCard
                    key={idx}
                    name={INDEX_DISPLAY_NAMES_EP[item.symbol] || item.name || item.symbol}
                    ticker={item.symbol}
                    price={item.price}
                    changePercent={item.changePercent}
                    change={item.change || 0}
                    onClick={() => navigate(`/option-chain?symbol=${item.symbol}`)}
                    liveData={liveData}
                    isLive={!!liveData}
                    marketOpen={foMarketOpen}
                  />
                );
              })}
            </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 text-center" data-testid="top-traded-empty">
                {fetchError ? (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Failed to load index data</p>
                    <button onClick={() => { retryCountRef.current = 0; fetchData(); }} className="text-sm text-[#00b386] font-medium hover:underline" data-testid="top-traded-retry">Retry now</button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Index data loading{foMarketOpen ? ' — retrying...' : ' — will appear when market opens'}</p>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">F&O Stocks</h2>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setStockFilter('gainers')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                    stockFilter === 'gainers' 
                      ? 'bg-gray-100 text-gray-700' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  data-testid="fo-stocks-gainers-btn"
                >
                  Top gainers
                </button>
                <button
                  onClick={() => setStockFilter('losers')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                    stockFilter === 'losers' 
                      ? 'bg-gray-100 text-gray-700' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                  data-testid="fo-stocks-losers-btn"
                >
                  Top losers
                </button>
              </div>
            </div>

            {filteredStocks.length > 0 ? (
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-100" data-testid="fo-stocks-table">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase tracking-wider">
                    <th className="py-2 px-4 text-left font-medium">Company</th>
                    <th className="py-2 px-4 text-right font-medium">Price</th>
                    <th className="py-2 px-4 text-right font-medium">Change</th>
                    <th className="py-2 px-4 text-right font-medium">Volume</th>
                    <th className="py-2 px-4 font-medium w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStocks.slice(0, 10).map((stock, idx) => {
                    const liveData = stockTicks[stock.ticker];
                    const livePrice = liveData?.last_price || stock.price;
                    const liveChangePct = (liveData?.change_percent && liveData.change_percent !== 0)
                      ? liveData.change_percent
                      : stock.changePercent;
                    const liveChange = liveData?.price_change ?? liveData?.change ?? (livePrice * liveChangePct / 100);
                    const item = {
                      symbol: stock.ticker,
                      name: stock.name,
                      price: livePrice,
                      change: liveChange,
                      change_percent: liveChangePct,
                      volume: liveData?.volume_traded || liveData?.volume || stock.volume || 0,
                      _live: !!liveData?.last_price,
                    };
                    return (
                      <InstrumentRow
                        key={stock.ticker}
                        item={item}
                        hovered={hoveredFoRow === stock.ticker}
                        onHover={setHoveredFoRow}
                        onClick={() => navigate(`/option-chain?symbol=${stock.ticker}`)}
                        showVolume
                        showActions
                        index={idx}
                        testIdPrefix="fo-stock"
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 text-center" data-testid="fo-stocks-empty">
                {fetchError ? (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Failed to load F&O stock data</p>
                    <button onClick={() => { retryCountRef.current = 0; fetchData(); }} className="text-sm text-[#00b386] font-medium hover:underline" data-testid="fo-stocks-retry">Retry now</button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">F&O stock data loading{foMarketOpen ? ' — retrying...' : ' — will appear when market opens'}</p>
                )}
              </div>
            )}
          </div>

          {/* Stock Futures + Index Futures + Positions Side Layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: Stock Futures + Index Futures stacked */}
            <div className="flex-1 min-w-0 space-y-8">
              {/* Top Traded Stock Futures */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Top traded stock futures</h2>
                </div>
                {sortedStockFutures.length > 0 ? (
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                  {sortedStockFutures.map((future, idx) => {
                    const liveData = futuresTicks[future.ticker];
                    const price = liveData?.last_price || future.ltp;
                    const liveChg = liveData?.change_percent;
                    const changePct = (liveChg && liveChg !== 0) ? liveChg : (future.change || 0);
                    const isPositive = (changePct || 0) >= 0;
                    const changeAbs = liveData?.price_change ?? (price * (changePct || 0) / 100);
                    return (
                      <div
                        key={future.ticker}
                        onClick={() => navigate(`/futures-trade?symbol=${future.ticker}${future.nfoExpiry ? `&expiry=${future.nfoExpiry}` : ''}`)}
                        className="flex-none w-44 bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
                        data-testid={`stock-future-card-${future.ticker}`}
                      >
                        <div className="flex justify-center mb-3">
                          <StockLogo symbol={future.ticker} name={future.name} size="lg" />
                        </div>
                        <p className="text-sm font-medium text-gray-800 text-center truncate">{future.name || future.ticker}</p>
                        <p className="text-[11px] text-gray-400 text-center mb-3">{future.ticker} · {future.expiry} Fut</p>
                        <p className="text-base font-bold text-gray-900 text-center">₹{formatPrice(price)}</p>
                        <p className={`text-xs font-medium text-center mt-1 ${isPositive ? 'text-[#00b386]' : 'text-[#eb5b3c]'}`}>
                          ₹{Math.abs(changeAbs)?.toFixed(2)} ({isPositive ? '+' : ''}{changePct?.toFixed(2)}%)
                        </p>
                      </div>
                    );
                  })}
                </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center" data-testid="stock-futures-empty">
                    {fetchError ? (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Failed to load stock futures data</p>
                        <button onClick={() => { retryCountRef.current = 0; fetchData(); }} className="text-sm text-[#00b386] font-medium hover:underline" data-testid="stock-futures-retry">Retry now</button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Stock futures data loading{foMarketOpen ? ' — retrying...' : ' — will appear when market opens'}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Top Traded Index Futures — always visible */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Top traded index futures</h2>
                </div>
                {sortedIndexFutures.length > 0 ? (
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                  {sortedIndexFutures.map((future, idx) => {
                    const liveData = indexFutTicks[future.ticker];
                    const price = liveData?.last_price || future.ltp;
                    const liveChg = liveData?.change_percent;
                    const changePct = (liveChg && liveChg !== 0) ? liveChg : (future.change || 0);
                    const isPositive = (changePct || 0) >= 0;
                    const changeAbs = liveData?.price_change ?? (price * (changePct || 0) / 100);
                    return (
                      <div
                        key={future.ticker}
                        onClick={() => navigate(`/futures-trade?symbol=${future.ticker}${future.nfoExpiry ? `&expiry=${future.nfoExpiry}` : ''}${future.nfoExchange ? `&exchange=${future.nfoExchange}` : ''}`)}
                        className="flex-none w-44 bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
                        data-testid={`index-future-card-${future.ticker}`}
                      >
                        <div className="flex justify-center mb-3">
                          <IndexLogo symbol={future.ticker} size="lg" />
                        </div>
                        <p className="text-sm font-medium text-gray-800 text-center truncate">{INDEX_DISPLAY_NAMES_EP[future.ticker] || future.ticker}</p>
                        <p className="text-[11px] text-gray-400 text-center mb-3">{future.ticker} · {future.expiry} Fut</p>
                        <p className="text-base font-bold text-gray-900 text-center">₹{formatPrice(price)}</p>
                        <p className={`text-xs font-medium text-center mt-1 ${isPositive ? 'text-[#00b386]' : 'text-[#eb5b3c]'}`}>
                          ₹{Math.abs(changeAbs)?.toFixed(2)} ({isPositive ? '+' : ''}{changePct?.toFixed(2)}%)
                        </p>
                      </div>
                    );
                  })}
                </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center" data-testid="index-futures-empty">
                    {fetchError ? (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Failed to load index futures data</p>
                        <button onClick={() => { retryCountRef.current = 0; fetchData(); }} className="text-sm text-[#00b386] font-medium hover:underline" data-testid="index-futures-retry">Retry now</button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Index futures data loading...</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Positions Box - Right Side, responsive */}
            <div className="w-full lg:w-96 lg:flex-shrink-0 flex flex-col" data-testid="fo-positions-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900">Positions</h2>
                <button 
                  onClick={() => navigate('/fo/positions')} 
                  className="text-base font-bold text-[#00b386] hover:underline"
                  data-testid="fo-positions-dashboard-link"
                >
                  Dashboard
                </button>
              </div>
              <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-8 flex flex-col justify-start">
                <p className="text-base text-gray-500 mb-2">F&O returns</p>
                <p className={`text-4xl font-bold tracking-tight transition-colors duration-300 ${livePositions.totalPnl >= 0 ? 'text-gray-800' : 'text-red-600'} ${totalPnlFlash === 'up' ? 'animate-pnl-flash-green' : totalPnlFlash === 'down' ? 'animate-pnl-flash-red' : ''}`} data-testid="fo-returns-value">
                  {livePositions.totalPnl >= 0 ? '' : '-'}₹{Math.abs(livePositions.totalPnl).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                {livePositions.positions.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <p className="text-sm font-medium text-gray-500">{livePositions.positions.length} active position{livePositions.positions.length !== 1 ? 's' : ''}</p>
                    {livePositions.positions.slice(0, 3).map((pos, idx) => (
                      <div key={pos.id || idx} className={`flex items-center justify-between py-2 border-b border-gray-100 last:border-0 transition-colors duration-300 ${pnlFlash[pos.symbol] === 'up' ? 'bg-green-50' : pnlFlash[pos.symbol] === 'down' ? 'bg-red-50' : ''}`} data-testid={`fo-position-row-${pos.symbol}`}>
                        <div>
                          <p className="text-sm font-medium text-gray-800 truncate max-w-[160px]">{pos.display_name || pos.symbol}</p>
                          <p className="text-xs text-gray-400">{pos.position_type === 'strategy' ? 'Strategy' : pos.quantity > 0 ? 'Long' : 'Short'} · Qty: {Math.abs(pos.quantity)}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold transition-colors duration-300 ${(pos.pnl || 0) >= 0 ? 'text-[#00b386]' : 'text-red-500'} ${pnlFlash[pos.symbol] === 'up' ? 'animate-pnl-flash-green' : pnlFlash[pos.symbol] === 'down' ? 'animate-pnl-flash-red' : ''}`}>
                            {(pos.pnl || 0) >= 0 ? '+' : ''}₹{Math.abs(pos.pnl || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {livePositions.positions.length > 3 && (
                      <button
                        onClick={() => navigate('/fo/positions')}
                        className="text-sm text-[#00b386] font-medium hover:underline w-full text-center pt-1"
                      >
                        View all {livePositions.positions.length} positions
                      </button>
                    )}
                  </div>
                )}
                {livePositions.positions.length === 0 && (
                  <p className="text-sm text-gray-400 mt-4">No active positions. Start trading to see your P&L here.</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Traded Commodity Futures - Card Layout */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Top traded commodity futures</h2>
            </div>
            {commodityFutures.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {sortedCommodityFutures.map((comm, idx) => {
                const liveTick = commTicks[comm.symbol];
                const price = liveTick?.last_price || comm.ltp;
                const liveChg = liveTick?.change_percent;
                const changePct = (liveChg && liveChg !== 0) ? liveChg : (comm.change || 0);
                const isPositive = (changePct || 0) >= 0;
                const changeAbs = liveTick?.price_change ?? (price * (changePct || 0) / 100);
                return (
                  <div
                    key={comm.symbol}
                    onClick={() => navigate(`/futures-trade?symbol=${comm.symbol}&exchange=MCX`)}
                    className="flex-none w-44 bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
                    data-testid={`commodity-future-card-${comm.symbol}`}
                  >
                    <div className="flex justify-center mb-3">
                      <CommodityLogo symbol={comm.symbol} size={48} />
                    </div>
                    <p className="text-sm font-medium text-gray-800 text-center truncate">{comm.name}</p>
                    <p className="text-[11px] text-gray-400 text-center mb-3">{comm.expiry ? `${comm.expiry} Fut` : `${comm.exchange || 'MCX'}`} &middot; {comm.lot_size} {comm.unit}</p>
                    <p className="text-base font-bold text-gray-900 text-center">₹{formatPrice(price)}</p>
                    <p className={`text-xs font-medium text-center mt-1 ${isPositive ? 'text-[#00b386]' : 'text-[#eb5b3c]'}`}>
                      ₹{Math.abs(changeAbs)?.toFixed(2)} ({isPositive ? '+' : ''}{changePct?.toFixed(2)}%)
                    </p>
                  </div>
                );
              })}
            </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-6 text-center" data-testid="commodity-futures-empty">
                {fetchError ? (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Failed to load commodity data</p>
                    <button onClick={() => { retryCountRef.current = 0; fetchData(); }} className="text-sm text-[#00b386] font-medium hover:underline" data-testid="commodity-futures-retry">Retry now</button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Commodity data loading{foMarketOpen ? ' — retrying...' : ' — will appear when MCX market opens'}</p>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Alert Creation Dialog for F&O Stocks */}
      <Dialog open={showFoAlertDialog} onOpenChange={setShowFoAlertDialog}>
        <DialogContent className="bg-white rounded-2xl max-w-md p-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-bold text-gray-900" data-testid="fo-alert-dialog-title">
              Create Price Alert — {foAlertSymbol}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Alert Type</label>
              <div className="flex gap-2">
                {["above", "below", "crosses"].map(t => (
                  <button key={t} onClick={() => setFoAlertType(t)}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border transition-colors capitalize ${foAlertType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    data-testid={`fo-alert-type-${t}`}
                  >{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Target Price</label>
              <Input type="number" value={foAlertPrice} onChange={(e) => setFoAlertPrice(e.target.value)}
                placeholder="Enter price" className="w-full" data-testid="fo-alert-price-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Note (optional)</label>
              <Input value={foAlertNote} onChange={(e) => setFoAlertNote(e.target.value)}
                placeholder="e.g., breakout level" className="w-full" data-testid="fo-alert-note-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleFoAlertSubmit} disabled={foAlertLoading || !foAlertPrice}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                data-testid="fo-alert-submit-btn">
                {foAlertLoading ? 'Creating...' : 'Create Alert'}
              </Button>
              <Button onClick={() => setShowFoAlertDialog(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
